import type { CSSProperties, ReactNode } from "react";

/**
 * Lightweight inline-SVG icon set (zero dependencies).
 * 24x24 viewBox, stroke = currentColor. Replaces the emoji icons used in the shell.
 */
export type IconName =
  | "dashboard" | "projects" | "customers" | "user" | "knowledge" | "components"
  | "funding" | "audit" | "admin" | "roof" | "pv" | "storage" | "simulation"
  | "economics" | "concepts" | "documents" | "monitoring"
  | "plus" | "search" | "chevronDown" | "chevronRight" | "chevronLeft"
  | "logout" | "menu" | "panelLeft" | "check" | "clock" | "bolt";

const ICONS: Record<IconName, ReactNode> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  projects: (
    <>
      <path d="M8 6h11" /><path d="M8 12h11" /><path d="M8 18h11" />
      <path d="M3.5 6h.01" /><path d="M3.5 12h.01" /><path d="M3.5 18h.01" />
    </>
  ),
  customers: (
    <>
      <path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
      <circle cx="9" cy="7" r="3.2" />
      <path d="M22 19v-1a4 4 0 0 0-3-3.87" />
      <path d="M16 4.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  user: (
    <>
      <path d="M19 20v-1a5 5 0 0 0-5-5h-4a5 5 0 0 0-5 5v1" />
      <circle cx="12" cy="8" r="3.5" />
    </>
  ),
  knowledge: (
    <>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </>
  ),
  components: (
    <>
      <path d="M12 2 3 7v10l9 5 9-5V7z" />
      <path d="M3 7l9 5 9-5" />
      <path d="M12 12v10" />
    </>
  ),
  funding: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 9v6" /><path d="M18 9v6" />
    </>
  ),
  audit: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8" /><path d="M8 12h8" />
      <path d="M8.5 16.5l1.4 1.4 2.8-2.8" />
    </>
  ),
  admin: (
    <>
      <path d="M4 6h9" /><path d="M19 6h1" /><circle cx="16" cy="6" r="2" />
      <path d="M4 12h2" /><path d="M11 12h9" /><circle cx="8.5" cy="12" r="2" />
      <path d="M4 18h9" /><path d="M19 18h1" /><circle cx="16" cy="18" r="2" />
    </>
  ),
  roof: (
    <>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M10 20v-6h4v6" />
    </>
  ),
  pv: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2.5" /><path d="M12 19.5V22" />
      <path d="M4.2 4.2l1.8 1.8" /><path d="M18 18l1.8 1.8" />
      <path d="M2 12h2.5" /><path d="M19.5 12H22" />
      <path d="M4.2 19.8l1.8-1.8" /><path d="M18 6l1.8-1.8" />
    </>
  ),
  storage: (
    <>
      <rect x="2" y="7" width="18" height="10" rx="2" />
      <path d="M22 10.5v3" />
      <path d="M6 11v2" /><path d="M10 11v2" /><path d="M14 11v2" />
    </>
  ),
  simulation: (
    <>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </>
  ),
  economics: (
    <>
      <path d="M3 3v18h18" />
      <rect x="7" y="11" width="3" height="6" />
      <rect x="12" y="8" width="3" height="9" />
      <rect x="17" y="5" width="3" height="12" />
    </>
  ),
  concepts: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6" /><path d="M9 17h5" />
    </>
  ),
  documents: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </>
  ),
  monitoring: (
    <>
      <path d="M5 18a8 8 0 1 1 14 0" />
      <path d="M12 14l3.5-3" />
      <circle cx="12" cy="14" r="1.2" />
    </>
  ),
  plus: (<><path d="M12 5v14" /><path d="M5 12h14" /></>),
  search: (<><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>),
  chevronDown: (<path d="M6 9l6 6 6-6" />),
  chevronRight: (<path d="M9 6l6 6-6 6" />),
  chevronLeft: (<path d="M15 6l-6 6 6 6" />),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
  menu: (<><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>),
  panelLeft: (<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16" /></>),
  check: (<path d="M5 13l4 4L19 7" />),
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  bolt: (<path d="M13 2L4.5 13.5H11l-1 8.5L18.5 10.5H12z" fill="currentColor" stroke="none" />),
};

interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}

export function Icon({ name, size = 20, strokeWidth = 1.8, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      {ICONS[name]}
    </svg>
  );
}
